import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import JointApplicationForm from './JointApplicationForm';
import CreditBureauRecheck from './CreditBureauRecheck';
import RestructureRequest from './RestructureRequest';

class ApiService {
	private baseUrl: string;

	constructor() {
		this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
	}

	async fetchSectors(): Promise<Sector[]> {
		const response = await fetch(`${this.baseUrl}/api/v1/sector`);
		if (!response.ok) throw new Error(`Failed to fetch sectors: ${response.status}`);
		const data = await response.json();
		return data.data.sectors.map((s: any) => ({ id: s._id, name: s.name }));
	}
}

const apiService = new ApiService();

interface Sector {
	id: string;
	name: string;
}

const useSectors = () => {
	const [sectors, setSectors] = useState<Sector[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	const loadSectors = async () => {
		try {
			setLoading(true);
			setError('');
			const data = await apiService.fetchSectors();
			setSectors(data);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch sectors');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadSectors();
	}, []);
	return { sectors, loading, error, retry: loadSectors };
};

const NAV_ITEMS = [
	{ path: '/', label: 'Loan Application' },
	{ path: '/credit-bureau-recheck', label: 'Credit Bureau Recheck' },
	{ path: '/restructure-request', label: 'Restructure Request' },
];

// ==================== MAIN COMPONENT ====================
const LoanApplicationForm = () => {
	const { sectors } = useSectors();
	const location = useLocation();
	const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

	return (
		<div className="min-h-screen bg-gray-50 p-4">
			<div className="max-w-7xl mx-auto space-y-6">
				{/* 1. Header */}
				<header className="bg-white border-b border-gray-200 px-6 py-4 rounded-lg shadow-sm border">
					<div className="max-w-7xl mx-auto flex justify-between items-center">
						<h1 className="text-2xl font-bold text-gray-900">Decision Engine Monitoring Tool</h1>
						<div className="flex gap-4">
							<span className="text-sm text-gray-500">FE v{process.env.REACT_APP_VERSION || '1.0.0'}</span>
						</div>
					</div>
				</header>

				{/* 2. Navigation */}
				<nav className="bg-white border border-gray-200 rounded-lg shadow-sm px-2 py-1 flex gap-1">
					{NAV_ITEMS.map((item) => {
						const isActive = location.pathname === item.path;
						return (
							<Link
								key={item.path}
								to={item.path}
								className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
									isActive
										? 'bg-blue-600 text-white'
										: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
								}`}>
								{item.label}
							</Link>
						);
					})}
				</nav>

				{/* 3. Page Content */}
				<Routes>
					<Route
						path="/"
						element={
							<>
								<header className="bg-gray-100 border-b border-gray-200 px-6 py-4 rounded-lg shadow-sm border">
									<div className="max-w-7xl mx-auto flex justify-center items-center">
										<h2 className="text-2xl font-extrabold tracking-wide text-blue-500">Loan Application</h2>
									</div>
								</header>
								<JointApplicationForm sectors={sectors} baseUrl={baseUrl} />
							</>
						}
					/>
					<Route
						path="/credit-bureau-recheck"
						element={
							<>
								<header className="bg-gray-100 border-b border-gray-200 px-6 py-4 rounded-lg shadow-sm border">
									<div className="max-w-7xl mx-auto flex justify-center items-center">
										<h2 className="text-2xl font-extrabold tracking-wide text-blue-500">Credit Bureau Recheck</h2>
									</div>
								</header>
								<CreditBureauRecheck baseUrl={baseUrl} />
							</>
						}
					/>
					<Route
						path="/restructure-request"
						element={
							<>
								<header className="bg-gray-100 border-b border-gray-200 px-6 py-4 rounded-lg shadow-sm border">
									<div className="max-w-7xl mx-auto flex justify-center items-center">
										<h2 className="text-2xl font-extrabold tracking-wide text-blue-500">Restructure Request</h2>
									</div>
								</header>
								<RestructureRequest baseUrl={baseUrl} />
							</>
						}
					/>
				</Routes>
			</div>
		</div>
	);
};

export default LoanApplicationForm;
